import {
  teamMemberTabScopeSchema,
  type TeamMemberSelectionEntry,
  type TeamMemberTabScope,
} from "@asap/shared/schemas/team-member-select";
import type { TeamMemberForAvatar } from "@asap/shared/utils/supabase.types";

/** Number of team members to load per page */
export const PAGE_SIZE = 20;

/** Sentinel row id for infinite-scroll loading row (not a real team member). */
export const TEAM_MEMBER_SELECT_LOADING_ROW_ID = "__loading_skeleton__";

/** Prefix for synthetic combobox values representing a global tab filter (not a team member id). */
export const TEAM_MEMBER_COMBO_FILTER_PREFIX = "__tm_filter__:";

/** Select-all scopes for the Managés tab only. */
export type ManageeScope = "managees_independents" | "managees_consultants";

export const manageesSelectAllConfig: { scope: ManageeScope; selectAllLabel: string; badgeLabel: string }[] = [
  {
    scope: "managees_consultants",
    selectAllLabel: "Sélectionner tous les salariés",
    badgeLabel: "Managés salariés",
  },
  {
    scope: "managees_independents",
    selectAllLabel: "Sélectionner tous les indépendants",
    badgeLabel: "Managés indépendants",
  },
];

export const isManageesGranularScope = (scope: TeamMemberTabScope): scope is ManageeScope =>
  scope === "managees_independents" || scope === "managees_consultants";

export const manageesGranularScopeBadgeLabel = (scope: ManageeScope): string =>
  manageesSelectAllConfig.find((config) => config.scope === scope)?.badgeLabel ?? scope;

export const otherManageeScope = (manageeScope: ManageeScope): ManageeScope =>
  manageeScope === "managees_independents" ? "managees_consultants" : "managees_independents";

const teamMemberFilterScopes = new Set<string>(teamMemberTabScopeSchema.options);

const isTeamMemberFilterScope = (scope: string): scope is TeamMemberTabScope => teamMemberFilterScopes.has(scope);

/**
 * Extended team member option with additional display properties
 */
export type TeamMemberOption = TeamMemberForAvatar & {
  label: string;
  is_independent: boolean | null;
  disabled?: boolean;
};

export type MemberMatchesScopeContext = {
  manageeIds: string[];
  isManageesLoaded: boolean;
  selectedIdsFallbackForManagees: string[];
};

/** Single source of truth: does this member belong to the given tab scope (same rules as list query). */
export function memberMatchesTabScope(
  tm: TeamMemberOption,
  scope: TeamMemberTabScope,
  ctx: MemberMatchesScopeContext
): boolean {
  if (scope === "managees") {
    if (!ctx.isManageesLoaded) return ctx.selectedIdsFallbackForManagees.includes(tm.id);
    return ctx.manageeIds.includes(tm.id);
  }
  if (scope === "managees_independents" || scope === "managees_consultants") {
    if (!memberMatchesTabScope(tm, "managees", ctx)) return false;
    if (scope === "managees_consultants") return tm.is_independent === false && tm.offboarded_at === null;
    return tm.is_independent === true && tm.offboarded_at === null;
  }
  if (scope === "consultants") return tm.is_independent === false && tm.offboarded_at === null;
  if (scope === "independents") return tm.is_independent === true && tm.offboarded_at === null;
  if (scope === "inactives") return tm.offboarded_at !== null;
  return false;
}

export const isManageesScopeSelected = (scopes: TeamMemberTabScope[], manageeScope: ManageeScope): boolean =>
  scopes.includes(manageeScope) || scopes.includes("managees");

const manageeGranularScopes = manageesSelectAllConfig.map((config) => config.scope);

const hasAllManageesGranularScopes = (scopes: TeamMemberTabScope[]): boolean =>
  manageeGranularScopes.every((scope) => scopes.includes(scope));

/** When both managee sub-scopes are selected, collapse to the single "managees" filter (badge: Moi & Managés). */
export const collapseManageesGranularFilters = (entries: TeamMemberSelectionEntry[]): TeamMemberSelectionEntry[] => {
  const filterScopes = entries
    .filter((entry): entry is { type: "filter"; value: TeamMemberTabScope } => entry.type === "filter")
    .map((entry) => entry.value);

  if (!hasAllManageesGranularScopes(filterScopes)) return entries;

  const withoutGranularManageeFilters = entries.filter(
    (entry) => !(entry.type === "filter" && isManageesGranularScope(entry.value))
  );

  if (withoutGranularManageeFilters.some((entry) => entry.type === "filter" && entry.value === "managees")) {
    return withoutGranularManageeFilters;
  }

  return [...withoutGranularManageeFilters, { type: "filter", value: "managees" }];
};

export type SelectAllOption = {
  scope: TeamMemberTabScope;
  label: string;
  checked: boolean;
};

export const buildSelectAllOptionsForActiveTab = (
  activeFilterTab: string,
  filterScopes: TeamMemberTabScope[],
  isMultiple: boolean
): SelectAllOption[] => {
  if (!isMultiple) return [];

  if (activeFilterTab === "managees") {
    return manageesSelectAllConfig.map((config) => ({
      scope: config.scope,
      label: config.selectAllLabel,
      checked: isManageesScopeSelected(filterScopes, config.scope),
    }));
  }

  const scope = parseTabValueToScope(activeFilterTab);
  if (scope === null) return [];

  return [{ scope, label: "Sélectionner tous", checked: filterScopes.includes(scope) }];
};

export type ApplyToggleSelectAllForScopeOptions = {
  selectionEntries: TeamMemberSelectionEntry[];
  scope: TeamMemberTabScope;
  checked: boolean | "indeterminate";
  filterScopes: TeamMemberTabScope[];
  teamMemberMatchesScope: (teamMember: TeamMemberOption, scope: TeamMemberTabScope) => boolean;
  getTeamMemberById: (id: string) => TeamMemberOption | undefined;
};

export const applyToggleSelectAllForScope = ({
  selectionEntries,
  scope,
  checked,
  filterScopes,
  teamMemberMatchesScope,
  getTeamMemberById,
}: ApplyToggleSelectAllForScopeOptions): TeamMemberSelectionEntry[] => {
  let next: TeamMemberSelectionEntry[];

  if (checked === true) {
    next = selectionEntries.filter((entry) => {
      if (entry.type === "filter") {
        if (entry.value === scope) return false;
        if (isManageesGranularScope(scope) && entry.value === "managees") return false;
        return true;
      }
      const teamMember = getTeamMemberById(entry.value);
      if (!teamMember) return true;
      return !teamMemberMatchesScope(teamMember, scope);
    });
    next.push({ type: "filter", value: scope });
  } else if (isManageesGranularScope(scope) && filterScopes.includes("managees")) {
    next = selectionEntries.filter(
      (entry) => !(entry.type === "filter" && (entry.value === "managees" || entry.value === scope))
    );
    next.push({ type: "filter", value: otherManageeScope(scope) });
  } else {
    next = selectionEntries.filter((entry) => !(entry.type === "filter" && entry.value === scope));
  }

  return collapseManageesGranularFilters(next);
};

export function parseTabValueToScope(tab: string): TeamMemberTabScope | null {
  if (tab === "consultants" || tab === "independents" || tab === "managees" || tab === "inactives") {
    return tab;
  }
  return null;
}

export function parseScopeFromComboboxFilterToken(token: string): TeamMemberTabScope | null {
  if (!token.startsWith(TEAM_MEMBER_COMBO_FILTER_PREFIX)) return null;
  const s = token.slice(TEAM_MEMBER_COMBO_FILTER_PREFIX.length);
  if (isTeamMemberFilterScope(s)) return s;
  return null;
}

export function encodeEntriesToComboboxTokens(entries: TeamMemberSelectionEntry[]): string[] {
  return entries.map((e) => (e.type === "id" ? e.value : `${TEAM_MEMBER_COMBO_FILTER_PREFIX}${e.value}`));
}

export function decodeComboboxTokens(vals: string[] | string | undefined): TeamMemberSelectionEntry[] {
  if (vals === undefined) return [];
  const arr = Array.isArray(vals) ? vals : [vals];
  const out: TeamMemberSelectionEntry[] = [];
  for (const v of arr) {
    if (v === TEAM_MEMBER_SELECT_LOADING_ROW_ID) continue;
    if (v.startsWith(TEAM_MEMBER_COMBO_FILTER_PREFIX)) {
      const scope = v.slice(TEAM_MEMBER_COMBO_FILTER_PREFIX.length);
      if (isTeamMemberFilterScope(scope)) {
        out.push({ type: "filter", value: scope });
      }
    } else {
      out.push({ type: "id", value: v });
    }
  }
  return out;
}

export function selectionEntriesEqual(
  a: TeamMemberSelectionEntry[] | undefined,
  b: TeamMemberSelectionEntry[]
): boolean {
  if (a === undefined) return b.length === 0;
  if (a.length !== b.length) return false;
  const key = (e: TeamMemberSelectionEntry) => (e.type === "id" ? `id:${e.value}` : `filter:${e.value}`);
  const aKeys = [...a].map(key).sort();
  const bKeys = [...b].map(key).sort();
  return aKeys.length === bKeys.length && aKeys.every((k, i) => k === bKeys[i]);
}

export type NormalizeFilterVsExplicitIdsOptions = {
  loadingSkeletonId: string;
  memberMatchesScope: (tm: TeamMemberOption, scope: TeamMemberTabScope) => boolean;
};

/**
 * When the combobox model includes both a global filter token and expanded ids for the same scope,
 * collapse back to `{ type: "filter" }` when the full visible list is selected; otherwise drop the
 * filter and keep explicit ids (partial selection).
 */
export function normalizeFilterVsExplicitIds(
  parsed: TeamMemberSelectionEntry[],
  listOptions: TeamMemberOption[],
  options: NormalizeFilterVsExplicitIdsOptions
): TeamMemberSelectionEntry[] {
  const { loadingSkeletonId, memberMatchesScope: matchScope } = options;

  const filters = parsed.filter((e): e is { type: "filter"; value: TeamMemberTabScope } => e.type === "filter");
  const idEntries = parsed.filter((e): e is { type: "id"; value: string } => e.type === "id");
  const idSet = new Set(idEntries.map((e) => e.value));

  const result: TeamMemberSelectionEntry[] = [];
  const idsToRemove = new Set<string>();

  for (const f of filters) {
    const scope = f.value;
    const fullExpanded = listOptions
      .filter((tm) => tm.id !== loadingSkeletonId && matchScope(tm, scope))
      .map((tm) => tm.id);
    const fullSet = new Set(fullExpanded);

    if (fullSet.size === 0) {
      result.push(f);
      continue;
    }

    const selectedInScope = fullExpanded.filter((id) => idSet.has(id));

    if (selectedInScope.length === fullSet.size) {
      result.push(f);
      for (const id of fullSet) idsToRemove.add(id);
      continue;
    }

    if (selectedInScope.length === 0) {
      result.push(f);
      continue;
    }
  }

  for (const e of idEntries) {
    if (!idsToRemove.has(e.value)) result.push(e);
  }

  return result;
}

export const loadingSkeletonTeamMember: TeamMemberOption = {
  id: TEAM_MEMBER_SELECT_LOADING_ROW_ID,
  label: "",
  is_independent: null,
  email: "",
  auth_user_id: "",
  workspace_id: "",
  main_workspace_id: "",
  management_level: 0,
  offboarded_at: null,
  manager_id: "",
  business_unit: { emoji: "", name: "" },
  auth_user: {
    id: "",
    first_name: null,
    last_name: null,
    email: "",
    phone_number: "",
    avatar_file: null,
    created_at: "",
    user_role: [],
  },
};
